import { useDebounce } from '@reactuses/core';
import { type Day, getDay, nextDay, startOfDay } from 'date-fns';
import { Building2, Home, Loader2, Search, User } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type ListHomeCareVendorsForPatients } from '@virtual-care/client';
import { SAUDI_CITIES } from '@virtual-care/isomorphic';
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@virtual-care/shadcn';
import { useData } from '@virtual-care/ui';

import { Header, ProfileCard } from '../../components/header';
import { VendorCard } from '../../components/homecare/vendor-card';

const WEEKDAY_INDICES = [6, 0, 1, 2, 3, 4, 5] as const;

function useLocalizedWeekdays() {
  const { i18n } = useTranslation();
  const formatter = new Intl.DateTimeFormat(i18n.language, { weekday: 'long' });
  const reference = new Date(2024, 0, 7);
  return WEEKDAY_INDICES.map((dayIndex) => ({
    dayIndex,
    label: formatter.format(
      new Date(reference.getTime() + dayIndex * 86400000),
    ),
  }));
}

type VendorTypeFilter = 'INDIVIDUAL' | 'CLINIC' | 'ALL';

function SearchBar({
  value,
  onChange,
  isLoading,
}: {
  value: string;
  onChange: (value: string) => void;
  isLoading: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="mb-4">
      <div className="relative">
        <Search className="absolute top-1/2 right-3 h-5 w-5 -translate-y-1/2 text-gray-400" />
        <Input
          type="text"
          placeholder={t('homecare.serviceListing.searchPlaceholder')}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="pr-10"
        />
        {isLoading && (
          <Loader2 className="text-primary absolute top-1/2 left-3 h-5 w-5 -translate-y-1/2 animate-spin" />
        )}
      </div>
    </div>
  );
}

function VendorTypeToggle({
  value,
  onChange,
}: {
  value: VendorTypeFilter;
  onChange: (value: VendorTypeFilter) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex gap-1 rounded-lg bg-gray-100 p-1">
      <Button
        size="sm"
        variant={value === 'ALL' ? 'default' : 'ghost'}
        onClick={() => onChange('ALL')}
        className="h-8 px-3 text-sm"
      >
        {t('common.all')}
      </Button>
      <Button
        size="sm"
        variant={value === 'INDIVIDUAL' ? 'default' : 'ghost'}
        onClick={() => onChange('INDIVIDUAL')}
        className="h-8 gap-1 px-3 text-sm"
      >
        <User className="h-3.5 w-3.5" />
        {t('homecare.serviceListing.vendorTypeIndividual')}
      </Button>
      <Button
        size="sm"
        variant={value === 'CLINIC' ? 'default' : 'ghost'}
        onClick={() => onChange('CLINIC')}
        className="h-8 gap-1 px-3 text-sm"
      >
        <Building2 className="h-3.5 w-3.5" />
        {t('homecare.serviceListing.vendorTypeClinic')}
      </Button>
    </div>
  );
}

function CityFilter({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-8 w-auto min-w-28">
        <SelectValue
          placeholder={t('homecare.serviceListing.cityPlaceholder')}
        />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="null">
          {t('homecare.serviceListing.allCities')}
        </SelectItem>
        {SAUDI_CITIES.map((city) => (
          <SelectItem key={city} value={city}>
            {city}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function GenderFilter({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-8 w-auto min-w-28">
        <SelectValue placeholder={t('common.gender')} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="null">{t('common.all')}</SelectItem>
        <SelectItem value="male">{t('common.male')}</SelectItem>
        <SelectItem value="female">{t('common.female')}</SelectItem>
      </SelectContent>
    </Select>
  );
}

function DayFilter({
  value,
  onChange,
}: {
  value: number | null;
  onChange: (value: number | null) => void;
}) {
  const { t } = useTranslation();
  const weekdays = useLocalizedWeekdays();
  return (
    <Select
      value={value?.toString() ?? 'null'}
      onValueChange={(v) => onChange(v === 'null' ? null : parseInt(v, 10))}
    >
      <SelectTrigger className="h-8 w-auto min-w-28">
        <SelectValue
          placeholder={t('homecare.serviceListing.dayPlaceholder')}
        />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="null">
          {t('homecare.serviceListing.allDays')}
        </SelectItem>
        {weekdays.map((day) => (
          <SelectItem key={day.dayIndex} value={day.dayIndex.toString()}>
            {day.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function EmptyState({
  searchQuery,
  hasActiveFilters,
  onClearFilters,
}: {
  searchQuery: string;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="py-12 text-center">
      <div className="mx-auto w-fit rounded-full bg-gray-100 p-4">
        <Home className="h-8 w-8 text-gray-400" />
      </div>
      <h3 className="mt-4 text-lg font-medium text-gray-900">
        {t('homecare.serviceListing.noResultsTitle')}
      </h3>
      <p className="mt-2 text-sm text-gray-600">
        {searchQuery
          ? t('homecare.serviceListing.noResultsForQuery', {
              query: searchQuery,
            })
          : t('homecare.serviceListing.noResultsWithFilters')}
      </p>
      {hasActiveFilters && (
        <Button variant="outline" onClick={onClearFilters} className="mt-4">
          {t('homecare.serviceListing.clearFilters')}
        </Button>
      )}
    </div>
  );
}

function VendorGrid({
  vendors,
}: {
  vendors: ListHomeCareVendorsForPatients['records'];
}) {
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {vendors.map((vendor) => (
        <VendorCard key={vendor.id} vendor={vendor} />
      ))}
    </div>
  );
}

export default function ServiceListing() {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [vendorType, setVendorType] = useState<VendorTypeFilter>('ALL');
  const [city, setCity] = useState<string>('null');
  const [selectedDayIndex, setSelectedDayIndex] = useState<number | null>(null);
  const [gender, setGender] = useState<string>('null');

  const debouncedSearchQuery = useDebounce(searchQuery, 400);

  const today = startOfDay(new Date());
  const filterDate =
    selectedDayIndex !== null
      ? getDay(today) === selectedDayIndex
        ? today
        : nextDay(today, selectedDayIndex as Day)
      : undefined;

  const { data, isLoading, isFetching } = useData(
    'GET /homecare/vendors/browse',
    {
      page: 1,
      pageSize: 50,
      query: debouncedSearchQuery || undefined,
      vendorType: vendorType === 'ALL' ? undefined : vendorType,
      city: city === 'null' ? undefined : city,
      gender:
        vendorType === 'INDIVIDUAL' && gender !== 'null'
          ? (gender as 'male' | 'female')
          : undefined,
      date: filterDate,
    },
  );

  const hasActiveFilters = Boolean(
    (city && city !== 'null') ||
    selectedDayIndex !== null ||
    (vendorType === 'INDIVIDUAL' && gender !== 'null'),
  );

  const clearFilters = () => {
    setCity('null');
    setGender('null');
    setSelectedDayIndex(null);
    setVendorType('ALL');
  };

  return (
    <>
      <Header
        title={
          <div className="flex items-center gap-2">
            <div className="rounded-full bg-green-600 p-2">
              <Home className="h-6 w-6 text-white" />
            </div>
            <h1 className="text-base font-bold text-gray-900 md:text-2xl">
              {t('homecare.pageTitle')}
            </h1>
          </div>
        }
      >
        <ProfileCard />
      </Header>

      <div className="min-h-screen px-4 pb-8">
        <div className="mx-auto max-w-6xl">
          <SearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            isLoading={isLoading || isFetching}
          />

          <div className="mb-6 flex flex-wrap items-center gap-2">
            <VendorTypeToggle value={vendorType} onChange={setVendorType} />
            <CityFilter value={city} onChange={setCity} />
            {vendorType === 'INDIVIDUAL' && (
              <GenderFilter value={gender} onChange={setGender} />
            )}
            <DayFilter
              value={selectedDayIndex}
              onChange={setSelectedDayIndex}
            />
          </div>

          {data?.records.length === 0 && !isLoading && !isFetching ? (
            <EmptyState
              searchQuery={searchQuery}
              hasActiveFilters={hasActiveFilters}
              onClearFilters={clearFilters}
            />
          ) : (
            <VendorGrid vendors={data?.records ?? []} />
          )}
        </div>
      </div>
    </>
  );
}
